import React, { createContext, ReactNode, useEffect, useContext, useMemo } from 'react';
import notificationService from '../../services/notificationService';
import { NotificationItemsContainer } from '../../services/types/Notification';
import useServiceReducer from '../../shared/hooks/useServiceReducer';
import { ApiState, Action } from '../../types';
import { serviceReducer } from '../../shared/hooks/serviceReducer';
import MainContext from '../App/MainProvider/context';

type NotificationProps = {
  children: ReactNode;
};

type NotificationProviderState = {
  data: NotificationItemsContainer;
  reload: () => void;
};

const initialProviderState: NotificationProviderState = {
  data: {} as NotificationItemsContainer,
  reload: () => {},
};

const refreshInterval = 30000;

const reducer = (
  state: ApiState<NotificationItemsContainer, undefined>,
  action: Action<NotificationItemsContainer, undefined>
): ApiState<NotificationItemsContainer, undefined> => serviceReducer(state, action);

const Context = createContext<NotificationProviderState>(initialProviderState);

const NotificationProvider: React.FC<NotificationProps> = ({ children }) => {
  const {
    user: { pollNotifications },
  } = useContext(MainContext);
  const [data, , , , , reload] = useServiceReducer(
    notificationService.lastNotifications,
    reducer,
    undefined
  );
  useEffect(() => {
    if (!pollNotifications) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      if (!document.hidden) {
        reload();
      }
    }, refreshInterval);

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        reload();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [pollNotifications, reload]);
  const value = useMemo(() => data && { data, reload }, [data, reload]);

  return value && <Context.Provider value={value}>{children}</Context.Provider>;
};

export default Context;

export { NotificationProvider };
